import classnames from "classnames";
import isEqual from "lodash-es/isEqual";
import React, { Component, ReactNode, useEffect, useState } from "react";
import Picker from "react-mobile-picker";
import ScrollLock from "react-scrolllock"; // TODO: WP-1418 - replace by body-scroll-lock package
import theme from "src/theme";
import styled from "styled-components";

import { Icon } from "..";
import { Mapping } from "../../types/primitives";
import { isLaptopOrUp } from "../RenderOn/RenderOn.component";

const TransparentMask = styled.div`
  position: fixed;
  overflow: hidden;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 1;
  display: none;
  width: 100%;
  height: 100%;

  &.show {
    display: block;
  }
`;

const DarkMask = styled(TransparentMask)`
  background: rgba(0, 0, 0, 0.6);
`;

const Modal = styled.div`
  position: fixed;
  left: 0;
  bottom: 0;
  z-index: 100;
  width: 100%;
  background-color: #efeff4;
  transform: translate(0, 100%);
  backface-visibility: hidden;
  transition: transform 0.3s;

  &.show {
    transform: translate(0, 0);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
    height: 40px;
    padding: 0 15px;
    background-color: #fbf9fe;
  }
`;

const TriggerComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const DefaultTriggerComponentWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
`;

const StyledIcon = styled(Icon)`
  margin-left: 3px;
  ${(props: Mapping): string => (props.disabled ? "display: none;" : "")}
`;

interface IProps<T> {
  className?: string;
  disabled?: boolean;
  onSelect: (value: T) => void;
  selectedValue: T;
  triggerComponent?: ReactNode[] | ReactNode;
  values: T[];
}

interface IState<T> {
  optionGroups: {
    values: T[];
  };
  showPicker: boolean;
  valueGroups: {
    values: T;
  };
}

const Container = styled.div`
  position: relative;
`;

const Select = styled.div`
  height: 50%;
  min-height: 200px;
  position: fixed;
  z-index: 1;
  overflow: auto;
  text-align: center;
  background-color: ${theme.colors.white};
  box-shadow: 1px 3px 5px ${theme.colors.shadow};

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  /* Hide scrollbar for IE and Edge */
  -ms-overflow-style: none;
  /* For Firefox */
  scrollbar-width: none;
`;

const Option = styled.div`
  cursor: pointer;
  padding: 3px;

  &.selected {
    background-color: rgba(0, 0, 0, 0.1);
  }
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

interface IDesktopPickerProps<T> {
  defaultValue: T;
  values: T[];
  onChange: (value: T) => void;
  show: boolean;
  togglePicker: () => void;
}

// tslint:disable-next-line: no-any
const DesktopPicker = <T extends any>({
  defaultValue,
  values,
  onChange,
  show,
  togglePicker,
}: IDesktopPickerProps<T>): JSX.Element => {
  const [value, setValue] = useState(defaultValue);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onChange(value), [value]);

  return (
    <Container>
      <TransparentMask
        className={classnames({ show })}
        onClick={togglePicker}
      />
      <Select hidden={!show}>
        {values.map((month, index) => (
          <Option
            key={index}
            className={values[index] === value ? "selected" : ""}
            onClick={(): void => {
              setValue(values[index]);
              togglePicker();
            }}
          >
            {month}
          </Option>
        ))}
      </Select>
    </Container>
  );
};

export default class CorePicker<T> extends Component<IProps<T>, IState<T>> {
  public state: IState<T> = {
    optionGroups: {
      values: this.props.values,
    },
    showPicker: false,
    valueGroups: {
      values: this.props.selectedValue,
    },
  };

  public closePicker = (): void => {
    this.setState({ showPicker: false });
  };

  public componentDidUpdate(prevProps: IProps<T>): void {
    const { values } = this.props;

    if (!isEqual(values, prevProps.values)) {
      this.setState({
        optionGroups: {
          values,
        },
      });
    }
  }

  public handleChange = (name: string, value: T): void => {
    this.setState(({ valueGroups }: { valueGroups: { values: T } }) => {
      const nextState = {
        valueGroups: {
          ...valueGroups,
          [name]: value,
        },
      };

      return nextState;
    });
  };

  public render(): ReactNode {
    const { className } = this.props;
    const { showPicker, optionGroups, valueGroups } = this.state;

    return (
      <div className={classnames("core-picker", className)}>
        <ScrollLock isActive={showPicker} />
        <TriggerComponentWrapper onClick={this.togglePicker}>
          {this.renderTriggerComponent()}
        </TriggerComponentWrapper>
        {isLaptopOrUp() ? (
          <div>
            <DesktopPicker
              show={showPicker}
              values={optionGroups.values}
              defaultValue={valueGroups.values}
              onChange={(value: T): void => {
                this.handleChange("values", value);
                this.props.onSelect(value);
              }}
              togglePicker={this.togglePicker}
            />
          </div>
        ) : (
          <div>
            <DarkMask
              className={classnames({ show: showPicker })}
              onClick={this.togglePicker}
            />
            <Modal className={classnames({ show: showPicker })}>
              <header>
                <div onClick={this.validate}>OK</div>
              </header>
              <Picker
                optionGroups={optionGroups}
                valueGroups={valueGroups}
                onChange={this.handleChange}
              />
            </Modal>
          </div>
        )}
      </div>
    );
  }

  public renderTriggerComponent = (): ReactNode | ReactNode[] => {
    const { selectedValue, triggerComponent, disabled } = this.props;

    if (!triggerComponent) {
      return (
        <DefaultTriggerComponentWrapper className="selected-option-wrapper">
          <div className="selected-option">{selectedValue}</div>
          <StyledIcon
            className="selected-option-button"
            name="chevron-bas"
            size={22}
            disabled={disabled}
          />
        </DefaultTriggerComponentWrapper>
      );
    }

    return triggerComponent;
  };

  public togglePicker = (): void => {
    if (!this.props.disabled) {
      this.setState((previousState: IState<T>) => ({
        showPicker: !previousState.showPicker,
      }));
    }
  };

  public validate = (): void => {
    this.togglePicker();
    this.props.onSelect(this.state.valueGroups.values);
  };
}
